import { Injectable } from '@angular/core';
import { AppConfigurationClient } from '@azure/app-configuration';

import { environment } from '../../environments/environment';

export interface AppConfig {
    production: boolean;
    apiBaseUrl: string;
    compositionApiBaseUrl: string;
    weatherWidgetUrlTemplate: string;
}

const APP_CONFIG_CONNECTION_STRING = '#{appConfigurationConnectionString}#';
const ENVIRONMENT_ALIAS = '#{environmentAlias}#';

const trimUrl = (url: string): string => (url || '').replace(/\/+$/, '') || window.location.origin;

@Injectable({ providedIn: 'root' })
export class ConfigService {
    private readonly _config: AppConfig = ConfigService.build();

    public get config(): AppConfig {
        return this._config;
    }

    public get apiBaseUrl(): string {
        return this._config.apiBaseUrl;
    }

    public get compositionApiBaseUrl(): string {
        return this._config.compositionApiBaseUrl;
    }

    public get<K extends keyof AppConfig>(key: K): AppConfig[K] {
        return this._config[key];
    }

    public static async load(): Promise<AppConfig> {
        if (APP_CONFIG_CONNECTION_STRING.includes('appConfigurationConnectionString')) {
            return ConfigService.build();
        }
        return ConfigService.buildFromAzure(APP_CONFIG_CONNECTION_STRING);
    }

    public static build(): AppConfig {
        return {
            production: environment.production,
            apiBaseUrl: trimUrl(environment.apiBaseUrl),
            compositionApiBaseUrl: trimUrl(environment.compositionApiBaseUrl),
            weatherWidgetUrlTemplate: environment.weatherWidgetUrlTemplate,
        };
    }

    private static async buildFromAzure(connectionString: string): Promise<AppConfig> {
        if (ENVIRONMENT_ALIAS.includes('environmentAlias')) {
            throw new Error('Environment alias is not configured for Azure App Configuration.');
        }

        const client = new AppConfigurationClient(connectionString);
        const settings = new Map<string, string>();
        const query = { keyFilter: 'reportService*', labelFilter: ENVIRONMENT_ALIAS };
        for await (const setting of client.listConfigurationSettings(query)) {
            if (setting.key) {
                settings.set(setting.key, setting.value ?? '');
            }
        }

        const apiBaseUrl = trimUrl(settings.get('reportServiceApiUrl') ?? '');
        return {
            production: true,
            apiBaseUrl,
            compositionApiBaseUrl: trimUrl(
                settings.get('reportServiceCompositionApiUrl') || apiBaseUrl,
            ),
            weatherWidgetUrlTemplate:
                settings.get('reportServiceWeatherWidgetUrlTemplate') ||
                environment.weatherWidgetUrlTemplate,
        };
    }
}
